'use client';

import * as React from 'react';
import { LuChevronDown, LuChevronUp } from 'react-icons/lu';

import { Box } from '../box';
import { Heading } from '../heading';
import { Text } from '../text';
import { HStack, VStack } from '../stack';
import { Collapsible } from '../collapsible';

export interface FormSectionProps {
  title: string;
  description?: string;
  icon?: React.ReactNode;
  collapsible?: boolean;
  defaultExpanded?: boolean;
  badge?: React.ReactNode;
  bordered?: boolean;
  children: React.ReactNode;
}

export function FormSection({
  title,
  description,
  icon,
  collapsible = false,
  defaultExpanded = true,
  badge,
  bordered = false,
  children,
}: FormSectionProps) {
  const [isOpen, setIsOpen] = React.useState(defaultExpanded);

  const headerContent = (
    <HStack justify="space-between" marginBottom={collapsible ? 0 : 3}>
      <HStack gap={2} align="center">
        {icon && <Box color="fg.muted">{icon}</Box>}
        <VStack gap={0.5} align="flex-start">
          <Heading as="h3" fontSize="md" fontWeight="semibold" color="fg.default">
            {title}
          </Heading>
          {description && (
            <Text fontSize="sm" color="fg.muted">
              {description}
            </Text>
          )}
        </VStack>
      </HStack>
      <HStack gap={2} align="center">
        {badge}
        {collapsible && (
          <Box color="fg.muted">
            {isOpen ? <LuChevronUp size={18} /> : <LuChevronDown size={18} />}
          </Box>
        )}
      </HStack>
    </HStack>
  );

  const content = collapsible ? (
    <Collapsible.Root defaultOpen={defaultExpanded} onOpenChange={setIsOpen}>
      <Collapsible.Trigger>{headerContent}</Collapsible.Trigger>
      <Collapsible.Content marginTop={3}>{children}</Collapsible.Content>
    </Collapsible.Root>
  ) : (
    <>
      {headerContent}
      {children}
    </>
  );

  return (
    <Box
      borderWidth={bordered ? '1px' : undefined}
      borderColor={bordered ? 'border.default' : undefined}
      borderRadius={bordered ? 'lg' : undefined}
      padding={bordered ? 4 : undefined}
    >
      {content}
    </Box>
  );
}

FormSection.displayName = 'FormSection';
